// ============================================================
// WEAPON NOISE - whether using it gives you away, and how far.
//
// THE SIGNATURE (plan armoury, stage 2): four steps - silent, muffled,
// loud, deafening. Unless its sheet says otherwise a melee weapon is
// silent and a ranged one loud. Each step but silent carries as far as the
// table's house rule says (10 / 50 / 150 m by default); silent stays at
// arm's length.
//
// ON THE WIRE the coarse word stays: `noiseOf` is 'silent' for silent and
// 'loud' for every other step, so a screen from before the ladder still
// reads every beat. The step rides beside it for the screens that know it.
//
// REACH ALONE DECIDES WHO HEARS (the user's ruling, 2026-10-19): a weapon
// heard by somebody of the opposition (within its reach) is loud for the
// ladder, exactly as before; beyond everyone's reach it is as quiet as a
// blade (`heardAs`).
//
// Pure. Zero React dependency.
// ============================================================

/** The coarse word every screen reads. */
export type Noise = 'silent' | 'loud';

export const NOISES: readonly Noise[] = ['silent', 'loud'];

/** The four steps a weapon may be set to. */
export type Signature = 'silent' | 'muffled' | 'loud' | 'deafening';

export const SIGNATURES: readonly Signature[] = ['silent', 'muffled', 'loud', 'deafening'];

interface Noisy {
  weaponType?: string;
  noise?: Signature;
}

/** What a weapon of this kind sounds like when its sheet does not say. */
export const defaultNoise = (weaponType: string | undefined): Noise => (weaponType === 'melee' ? 'silent' : 'loud');

/** This weapon's step: its own, else its kind's. A word from nowhere reads as its kind's. */
export function signatureOf(slot: Noisy | null | undefined): Signature {
  return SIGNATURES.includes(slot?.noise as Signature) ? (slot!.noise as Signature) : defaultNoise(slot?.weaponType);
}

/** The coarse word on the wire: only silent is silent. */
export function noiseOf(slot: Noisy | null | undefined): Noise {
  return signatureOf(slot) === 'silent' ? 'silent' : 'loud';
}

/** How far each audible step carries, metres. */
export interface SignatureReach {
  muffled: number;
  loud: number;
  deafening: number;
}

/** The table's choices (`signatureReach`, a house rule), tightest first. */
export const SIGNATURE_REACHES = [
  { key: 'tight', label: 'Muffled 5 m · Loud 30 m · Deafening 100 m', reach: { muffled: 5, loud: 30, deafening: 100 } },
  {
    key: 'standard',
    label: 'Muffled 10 m · Loud 50 m · Deafening 150 m',
    hint: 'A room, a street, a district.',
    reach: { muffled: 10, loud: 50, deafening: 150 },
  },
  {
    key: 'wide',
    label: 'Muffled 20 m · Loud 100 m · Deafening 300 m',
    reach: { muffled: 20, loud: 100, deafening: 300 },
  },
] as const;

export type SignatureReachKey = (typeof SIGNATURE_REACHES)[number]['key'];
export const DEFAULT_SIGNATURE_REACH_KEY: SignatureReachKey = 'standard';
export const DEFAULT_SIGNATURE_REACH: SignatureReach = { muffled: 10, loud: 50, deafening: 150 };

export const isSignatureReachKey = (v: unknown): v is SignatureReachKey => SIGNATURE_REACHES.some((c) => c.key === v);

/** The reach table for a stored choice; anything else is the standard one. */
export function signatureReachOf(key: unknown): SignatureReach {
  const hit = SIGNATURE_REACHES.find((c) => c.key === key);
  return hit ? { ...hit.reach } : { ...DEFAULT_SIGNATURE_REACH };
}

/** A silent weapon is heard only at arm's length - the ring that does not spread. */
const SILENT_REACH_M = 1.5;

/** How far this step carries at this table, metres. */
export const reachOf = (step: Signature, reach: SignatureReach): number =>
  step === 'silent' ? SILENT_REACH_M : reach[step];

interface At {
  x: number;
  z: number;
}

/**
 * How the ladder hears an attack: loud when anybody listening stands within
 * the weapon's reach, silent when nobody does. With nowhere to measure from
 * (no map, the attacker off it, no listener on it) the step decides alone.
 */
export function heardAs(step: Signature, reach: SignatureReach, from: At | null, listeners: readonly At[]): Noise {
  if (step === 'silent') return 'silent';
  if (!from || listeners.length === 0) return 'loud';
  const r = reachOf(step, reach);
  return listeners.some((l) => Math.hypot(l.x - from.x, l.z - from.z) <= r) ? 'loud' : 'silent';
}
